/**
 * Client AccessLevel semantics — the single typed source of truth for what the
 * three `AccessLevel` values mean for a `client_user`, mirrored by the RLS
 * migration `20260619130000_client_access_levels_ticket_create_gate.sql`.
 *
 * | Level       | Data scope                         | Can create tickets? | Provisioned as        |
 * |-------------|------------------------------------|---------------------|-----------------------|
 * | `full`      | ALL company data (every contact)   | YES                 | company_user_access   |
 * | `read_only` | ALL company data (same as full)    | NO (read-only)      | company_user_access   |
 * | `limited`   | ONLY the single granted contact    | YES                 | contact_user_access   |
 *
 * READ access is enforced by the SQL `has_contact_access()` helper, which ORs an
 * active `contact_user_access` (single contact) with an active
 * `company_user_access` (whole company). Both `full` and `read_only` write a
 * `company_user_access` row, so both grant the company-wide view; the ONLY
 * difference is the ticket-create gate (`read_only` cannot create).
 *
 * See `.claude/rules/USER_ROLES_AND_ACCESS.md`.
 */

import type { AccessLevel } from "@/lib/types/database";

export const ACCESS_LEVEL = {
  /** Company-wide data access + ticket creation. */
  FULL: "full",
  /** Single granted contact only + ticket creation. */
  LIMITED: "limited",
  /** Company-wide data access, read-only — cannot create tickets. */
  READ_ONLY: "read_only",
} as const;

/** Whether a grant at this level scopes to the WHOLE company (vs a single contact). */
export function isCompanyWideAccessLevel(level: AccessLevel): boolean {
  return level === ACCESS_LEVEL.FULL || level === ACCESS_LEVEL.READ_ONLY;
}

/** Whether a client holding this level may create tickets. read_only may NOT. */
export function canCreateTicketsAtLevel(level: AccessLevel): boolean {
  return level !== ACCESS_LEVEL.READ_ONLY;
}

/** UI/grant metadata for each access level — labels + accurate descriptions. */
export interface AccessLevelOption {
  value: AccessLevel;
  label: string;
  /** Short, accurate description of exactly what this level grants. */
  description: string;
}

/** Human label for a stored access_level value. */
export function getAccessLevelLabel(level: string): string {
  const match = ACCESS_LEVEL_OPTIONS.find((option) => option.value === level);
  return match?.label ?? level.replace(/_/g, " ");
}

/** Short scope sentence for admin UI (grant preview + existing grants). */
export function describeClientAccessScope(
  level: AccessLevel,
  context: { contactName?: string | null; companyName?: string | null }
): string {
  const contact = context.contactName?.trim() || "the selected contact";
  const company = context.companyName?.trim() || "this contact's company";

  if (level === ACCESS_LEVEL.LIMITED) {
    return `Only ${contact} — tickets, communications, and time for that one person. Other contacts at ${company} stay hidden.`;
  }
  if (level === ACCESS_LEVEL.READ_ONLY) {
    return `Every contact at ${company} (current and future) — view-only. Cannot create new tickets.`;
  }
  return `Every contact at ${company} (current and future) — full portal access including creating tickets.`;
}

export const ACCESS_LEVEL_OPTIONS: readonly AccessLevelOption[] = [
  {
    value: "full",
    label: "Full (company-wide)",
    description:
      "Access to ALL of the company's data — tickets, time entries, and contacts for every contact at the company. Can create new tickets.",
  },
  {
    value: "limited",
    label: "Limited (this contact only)",
    description:
      "Access to ONLY this one contact's data. Can create tickets for this contact.",
  },
  {
    value: "read_only",
    label: "Read-only (company-wide, view only)",
    description:
      "Company-wide view of all the company's data (like Full), but CANNOT create new tickets — read-only.",
  },
] as const;
